// Entity hover component for LEGO items (sets, parts, minifigs, elements)

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { trackLinkClick } from '../services/linkTracking';
import { type EntityTypePlural, toSingular } from '../types/entities';
import { type EntityDetailsResponse, type SuggestionLink, type EntityData } from '../types/entityData';
import { getPlatformFromUrl } from '../utils/linkUtils';
import ImageWithPlaceholder from './ImageWithPlaceholder';
import '../styles/ImagePlaceholder.css';
import {LoadingAnimation} from "./LoadingAnimation.tsx";

interface EntityHoverProps {
  type: EntityTypePlural;
  id: string;
  colorId?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  onSuggestionClick?: (suggestion: string) => void;
}

/**
 * Get entity title from structured data.
 */
const getTitle = (data: EntityData): string => {
  if ('name' in data) return data.name;
  return '';
};

/**
 * Get entity image URL from structured data.
 */
const getImageUrl = (data: EntityData): string | undefined => {
  if ('imageUrl' in data) return data.imageUrl as string | undefined;
  return undefined;
};

const EntityHover: React.FC<EntityHoverProps> = ({
  type,
  id,
  colorId,
  children,
  className = '',
  onClick,
  onSuggestionClick
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [structuredData, setStructuredData] = useState<EntityData | null>(null);
  const [structuredSuggestions, setStructuredSuggestions] = useState<SuggestionLink[]>([]);
  // Keep legacy fields for backward compatibility during transition
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardPosition, setCardPosition] = useState<'above' | 'below'>('below');
  const [cardCoords, setCardCoords] = useState<{ top: number; left: number } | null>(null);

  // Detect if device is mobile/touch-enabled to disable hover
  const isMobile = useRef<boolean>(
    'ontouchstart' in window || navigator.maxTouchPoints > 0
  ).current;

  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  /**
   * Handle external link click with tracking
   */
  const handleExternalLinkClick = (url: string) => {
    const platform = getPlatformFromUrl(url);
    if (platform) {
      trackLinkClick({
        entityType: toSingular(type),
        entityId: id,
        platform,
        url,
        referrer: 'hover-card',
      });
    }
  };

  const fetchEntityData = useCallback(async (signal: AbortSignal) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('context', 'hover');
      if (colorId) {
        params.append('colorId', colorId);
      }
      const queryString = params.toString();
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/entities/${type}/${id}${queryString ? '?' + queryString : ''}`;

      const token = authService.getToken();
      const headers: HeadersInit = {
        'Accept': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(apiUrl, { headers, signal });

      if (signal.aborted) return;

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch entity data: ${response.status}`);
      }

      const data: EntityDetailsResponse = await response.json();

      if (!signal.aborted) {
        // Prefer structured data when available (#435)
        if (data.structuredData) {
          setStructuredData(data.structuredData);
        }
        if (data.structuredSuggestions) {
          setStructuredSuggestions(data.structuredSuggestions);
        }
        // Keep legacy field for backward compat
        setSummary(data.summary);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (!signal.aborted) {
        setError('Failed to load details');
        console.error('Error fetching entity data:', err);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [type, id, colorId]);

  useEffect(() => {
    if (isMobile) return;

    const abortController = new AbortController();

    if (isHovering) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      if (showCard) return;

      showTimeoutRef.current = setTimeout(() => {
        if (!isHovering) return;

        setShowCard(true);
        fetchEntityData(abortController.signal);

        if (linkRef.current) {
          const rect = linkRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          const estimatedCardHeight = 450;
          const gap = 8;

          const position = spaceBelow < estimatedCardHeight && spaceAbove > spaceBelow ? 'above' : 'below';
          setCardPosition(position);

          const left = rect.left + window.scrollX;
          let top: number;
          if (position === 'above') {
            top = rect.top + window.scrollY;
          } else {
            top = rect.bottom + window.scrollY + gap;
            const cardBottom = top + estimatedCardHeight;
            const screenBottom = window.innerHeight + window.scrollY;
            if (cardBottom > screenBottom) {
              top = Math.max(gap, screenBottom - estimatedCardHeight - gap);
            }
          }

          setCardCoords({ top, left });
        }
      }, 300);
    } else {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }

      hideTimeoutRef.current = setTimeout(() => {
        setShowCard(false);
        setStructuredData(null);
        setStructuredSuggestions([]);
        setSummary(null);
        setError(null);
      }, 200);
    }

    return () => {
      abortController.abort();
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [isHovering, isMobile, fetchEntityData]);

  /**
   * Render suggestion items from structured suggestions.
   */
  const renderSuggestions = () => {
    if (structuredSuggestions.length === 0) return null;

    return (
      <div className="entity-hover-suggestions">
        {structuredSuggestions.map((suggestion, index) => {
          if (suggestion.type === 'external' && suggestion.url) {
            return (
              <a
                key={index}
                href={suggestion.url}
                target="_blank"
                rel="noopener noreferrer"
                className="entity-hover-suggestion-link"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExternalLinkClick(suggestion.url!);
                }}
              >
                {suggestion.emoji ? `${suggestion.emoji} ` : ''}{suggestion.text}
              </a>
            );
          }

          return (
            <button
              key={index}
              className="entity-hover-suggestion-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onSuggestionClick) {
                  onSuggestionClick(suggestion.text);
                }
              }}
            >
              {suggestion.text}
            </button>
          );
        })}
      </div>
    );
  };

  /**
   * Render card content using structured data (no regex parsing).
   */
  const renderStructuredContent = () => {
    if (!structuredData) return null;

    const title = getTitle(structuredData);
    const imageUrl = getImageUrl(structuredData);

    return (
      <>
        <div className="entity-hover-title">
          <h3>{title}</h3>
        </div>

        <div className="entity-hover-split">
          <div className="entity-hover-images">
            {imageUrl && (
              <ImageWithPlaceholder
                src={imageUrl}
                alt={title}
              />
            )}
          </div>

          {renderSuggestions()}
        </div>

        {/* Additional content below split - always render for test compatibility */}
        <div className="entity-hover-content">
          {/* Structured data summary info */}
          {'totalParts' in structuredData && (
            <p>
              {'year' in structuredData && structuredData.year && <span>{structuredData.year} · </span>}
              {structuredData.totalParts.toLocaleString()} parts
              {'totalMinifigures' in structuredData && structuredData.totalMinifigures > 0 && (
                <span> · {structuredData.totalMinifigures} minifigures</span>
              )}
              {'theme' in structuredData && structuredData.theme && (
                <span> · {structuredData.theme}</span>
              )}
            </p>
          )}
          {'category' in structuredData && structuredData.category && (
            <p>
              {structuredData.category}
              {'material' in structuredData && structuredData.material && (
                <span> · {structuredData.material}</span>
              )}
              {'uniqueColors' in structuredData && structuredData.uniqueColors && (
                <span> · {structuredData.uniqueColors} colors</span>
              )}
            </p>
          )}
          {'numParts' in structuredData && structuredData.numParts && (
            <p>{structuredData.numParts} parts</p>
          )}
          {'colorName' in structuredData && (
            <p>
              {structuredData.partName} · {structuredData.colorName}
              {'category' in structuredData && structuredData.category && (
                <span> · {structuredData.category}</span>
              )}
            </p>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <a
        ref={linkRef}
        href={`/items/${type}/${id}${colorId ? `?color=${colorId}` : ''}`}
        className={`entity-link ${className}`}
        onClick={onClick}
        onMouseEnter={isMobile ? undefined : () => setIsHovering(true)}
        onMouseLeave={isMobile ? undefined : () => setIsHovering(false)}
      >
        {children}
      </a>
      {!isMobile && showCard && cardCoords && (
        <div
          className={`entity-hover-card entity-hover-card-${cardPosition}`}
          style={{
            top: `${cardCoords.top}px`,
            left: `${cardCoords.left}px`,
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {loading && (
            <div className="entity-hover-loading">
              <LoadingAnimation size={120} message="Loading details" />
            </div>
          )}

          {error && (
            <div className="entity-hover-error">
              {error}
            </div>
          )}

          {!loading && !error && structuredData && renderStructuredContent()}

          {/* Fallback: render legacy markdown summary if no structured data */}
          {!loading && !error && !structuredData && summary && (() => {
            // Legacy rendering path (backward compat)
            const headingMatch = summary.match(/^#\s+(.+?)$/m);
            const title = headingMatch ? headingMatch[1] : `${toSingular(type)} ${id}`;
            const imageRegex = /!\[.*?]\((.*?)\)/g;
            const images: string[] = [];
            let match;
            while ((match = imageRegex.exec(summary)) !== null) {
              images.push(match[1]);
            }

            return (
              <>
                <div className="entity-hover-title">
                  <h3>{title}</h3>
                </div>
                <div className="entity-hover-split">
                  <div className="entity-hover-images">
                    {images.map((imgSrc, index) => (
                      <ImageWithPlaceholder key={index} src={imgSrc} alt={`${title} - Image ${index + 1}`} />
                    ))}
                  </div>
                  {renderSuggestions()}
                </div>
                <div className="entity-hover-content" />
              </>
            );
          })()}
        </div>
      )}
    </>
  );
};

export default EntityHover;
